import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useConfig } from './ConfigContext';

const BoutiqueContext = createContext();

export const useBoutique = () => {
  const context = useContext(BoutiqueContext);
  if (!context) {
    throw new Error('useBoutique doit être utilisé dans un BoutiqueProvider');
  }
  return context;
};

export const BoutiqueProvider = ({ children }) => {
  const { user } = useAuth();
  const [boutiques, setBoutiques] = useState([]);
  const [selectedBoutiqueId, setSelectedBoutiqueId] = useState('all'); // 'all' ou un ID spécifique
  const [loading, setLoading] = useState(true);
  // Boutique par défaut de l'entreprise (Configuration) — appliquée aux rôles
  // multi-boutiques ; les autres rôles restent sur leur propre boutique.
  const { config } = useConfig();
  const boutiqueDefaut = config?.boutique_defaut || 'all';
  const defautApplique = useRef(null);

  useEffect(() => {
    if (user) {
      loadBoutiques();
    }
  }, [user]);

  useEffect(() => {
    if (!user || ['admin-boutique', 'gestionnaire', 'caissier'].includes(user.role)) return;
    if (!boutiques.length || defautApplique.current === boutiqueDefaut) return;
    defautApplique.current = boutiqueDefaut;
    const valide = boutiqueDefaut === 'all' || boutiques.some(b => b._id === boutiqueDefaut);
    if (valide) setSelectedBoutiqueId(boutiqueDefaut);
  }, [user, boutiques, boutiqueDefaut]);

  const loadBoutiques = async () => {
    try {
      const { data } = await axios.get('/api/boutiques');
      setBoutiques(data);
      
      // Si admin-boutique, gestionnaire ou caissier → sélectionner automatiquement leur boutique
      if (user && ['admin-boutique', 'gestionnaire', 'caissier'].includes(user.role)) {
        setSelectedBoutiqueId(user.boutiqueId || 'all');
      }
    } catch (error) {
      console.error('Erreur chargement boutiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectBoutique = (boutiqueId) => {
    setSelectedBoutiqueId(boutiqueId);
  };

  const getSelectedBoutique = () => {
    if (selectedBoutiqueId === 'all') return null;
    return boutiques.find(b => b._id === selectedBoutiqueId);
  };

  const isAllBoutiques = () => selectedBoutiqueId === 'all';

  // Filtrer les données par boutique sélectionnée
  const filterByBoutique = (items) => {
    if (selectedBoutiqueId === 'all') return items;
    return items.filter(item => item.boutiqueId === selectedBoutiqueId);
  };

  const value = {
    boutiques,
    selectedBoutiqueId,
    selectBoutique,
    getSelectedBoutique,
    isAllBoutiques,
    filterByBoutique,
    loading,
    reloadBoutiques: loadBoutiques
  };

  return (
    <BoutiqueContext.Provider value={value}>
      {children}
    </BoutiqueContext.Provider>
  );
};
